import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { fileDownloadUrl } from "./FileUploader";
import { FileText, Download, Trash2, ShieldCheck, Clock, AlertTriangle, Image as ImageIcon, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

function formatSize(bytes) {
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function IconForType({ ctype }) {
  if ((ctype || "").startsWith("image/")) return <ImageIcon className="w-4 h-4 text-amber-500/70" />;
  return <FileText className="w-4 h-4 text-amber-500/70" />;
}

function ExpiryBadge({ expiry }) {
  if (!expiry) return null;
  const now = new Date();
  const d = new Date(expiry);
  const days = Math.floor((d - now) / 86400000);
  const cls = days < 0 ? "stamp-bad" : days < 30 ? "stamp-warn" : "stamp-ok";
  const label = days < 0 ? "EXPIRED" : days < 30 ? `${days}d LEFT` : "VALID";
  return (
    <span className={`stamp ${cls}`} data-testid="expiry-badge">
      <Clock className="w-3 h-3" /> {label}
    </span>
  );
}

/**
 * Reusable file list. Auth-only downloads.
 * scope + scopeId filter server-side.
 * showCert: display cert_type + expiry columns.
 * emptyLabel: message when no files.
 */
export default function FileList({ scope, scopeId, showCert, emptyLabel, refreshKey }) {
  const { user } = useAuth();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);

  const load = useCallback(async () => {
    try {
      const params = {};
      if (scope) params.scope = scope;
      if (scopeId) params.scope_id = scopeId;
      const { data } = await api.get("/files", { params });
      setFiles(data);
    } catch { setFiles([]); }
    finally { setLoading(false); }
  }, [scope, scopeId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const download = async (f) => {
    setDownloading(f.id);
    try {
      const res = await api.get(`/files/${f.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = f.filename || "file";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
    finally { setDownloading(null); }
  };

  const del = async (f) => {
    if (!window.confirm(`Delete ${f.filename}?`)) return;
    try {
      await api.delete(`/files/${f.id}`);
      toast.success("Removed");
      load();
    } catch { toast.error("Delete failed"); }
  };

  if (loading) {
    return <div className="font-mono-data text-xs text-text-secondary animate-pulse py-3">Loading files...</div>;
  }
  if (files.length === 0) {
    return (
      <div className="tray-inset p-4 text-center font-mono-data text-xs text-text-secondary" data-testid={`files-empty-${scope || 'any'}`}>
        {emptyLabel || "No files yet"}
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid={`file-list-${scope || 'any'}`}>
      {files.map(f => (
        <div key={f.id} className="tray-inset p-3 flex items-center gap-3" data-testid={`file-row-${f.id}`}>
          <IconForType ctype={f.content_type} />
          <div className="flex-1 min-w-0">
            <div className="text-sm truncate">{f.filename}</div>
            <div className="font-mono-data text-[10px] text-text-secondary flex items-center gap-2 mt-0.5">
              <span>{formatSize(f.size)}</span>
              <span>·</span>
              <span>{new Date(f.created_at).toLocaleDateString()}</span>
              {f.owner_name && <><span>·</span><span className="truncate">{f.owner_name}</span></>}
            </div>
            {showCert && (f.cert_type || f.expiry_date) && (
              <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                {f.cert_type && (
                  <span className="font-mono-data text-[9px] tracking-widest px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-500 inline-flex items-center gap-1">
                    <ShieldCheck className="w-3 h-3" /> {f.cert_type}
                  </span>
                )}
                <ExpiryBadge expiry={f.expiry_date} />
                {f.expiry_date && new Date(f.expiry_date) < new Date() && (
                  <span className="text-red-400 text-[10px] font-mono-data inline-flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" /> Renew required
                  </span>
                )}
              </div>
            )}
          </div>
          <button
            onClick={() => download(f)}
            disabled={downloading === f.id}
            data-testid={`file-download-${f.id}`}
            className="text-text-secondary hover:text-amber-500"
            title="Download"
          >
            {downloading === f.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
          </button>
          {user && (f.owner_id === user.user_id || user.is_admin) && (
            <button
              onClick={() => del(f)}
              data-testid={`file-delete-${f.id}`}
              className="text-text-secondary hover:text-red-400"
              title="Delete"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

// Convenience wrapper for HSE certs display + upload
export { ExpiryBadge };
